import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { request } from "@/api/client";

// Backend serialises Decimal as a string (e.g. "12.34") — coerce to number
// at the boundary so consumers don't have to parseFloat everywhere.
const decimalString = z.coerce.number();

export const SummarySchema = z.object({
  revenue_usd: decimalString,
  llm_cost_usd: decimalString,
  contribution_usd: decimalString,
  contribution_margin: decimalString,
  paying_users: z.number().int().nonnegative(),
  free_users: z.number().int().nonnegative(),
  conversion_rate: decimalString,
  break_even_conversion: decimalString.nullable(),
  arpu_paying: decimalString,
  cost_per_free: decimalString,
  // DUE-529 — lens-reading cost per active relation/words subscriber.
  lens_cost_usd: decimalString,
  active_subscribers: z.number().int().nonnegative(),
  lens_cost_per_subscriber_usd: decimalString,
  // DUE-492 — App Store payments whose receipt currency has no APPSTORE_USD_RATES entry.
  appstore_unconverted: z.object({
    count: z.number().int().nonnegative(),
    by_currency: z.record(
      z.object({ count: z.number().int().nonnegative(), amount_local: decimalString }),
    ),
  }),
});
export type Summary = z.infer<typeof SummarySchema>;

export const TimeseriesPointSchema = z.object({
  date: z.string(),
  revenue_usd: decimalString,
  llm_cost_usd: decimalString,
  net_usd: decimalString,
});
export const TimeseriesSchema = z.array(TimeseriesPointSchema);
export type TimeseriesPoint = z.infer<typeof TimeseriesPointSchema>;

export const SKURowSchema = z.object({
  code: z.string(),
  purchases: z.number().int().nonnegative(),
  revenue_usd: decimalString,
  attributable_cost_usd: decimalString,
});
export const SKUListSchema = z.array(SKURowSchema);
export type SKURow = z.infer<typeof SKURowSchema>;

// DUE-291/293/295 — LLM cost per `readings.type`; `distinct_pairs` /
// `cost_per_pair_usd` are null for solo types (natal, transit_day, …).
export const ReadingTypeCostRowSchema = z.object({
  reading_type: z.string(),
  readings: z.number().int().nonnegative(),
  tokens_in: z.number().int().nonnegative(),
  tokens_out: z.number().int().nonnegative(),
  cost_usd: decimalString,
  distinct_pairs: z.number().int().nonnegative().nullable(),
  cost_per_pair_usd: decimalString.nullable(),
});
export const ReadingTypeCostListSchema = z.array(ReadingTypeCostRowSchema);
export type ReadingTypeCostRow = z.infer<typeof ReadingTypeCostRowSchema>;

export const CohortBucketSchema = z.object({
  revenue: decimalString,
  cost: decimalString,
  net: decimalString,
});
export const CohortRowSchema = z.object({
  cohort_week: z.string(),
  cohort_size: z.number().int().nonnegative(),
  day_0: CohortBucketSchema,
  day_7: CohortBucketSchema,
  day_14: CohortBucketSchema,
  day_30: CohortBucketSchema,
});
export const CohortsSchema = z.array(CohortRowSchema);
export type CohortRow = z.infer<typeof CohortRowSchema>;

export const CompositeFunnelSchema = z.object({
  composite_purchases: z.number().int().nonnegative(),
  composite_opened: z.number().int().nonnegative(),
  composite_open_rate: decimalString,
});
export type CompositeFunnel = z.infer<typeof CompositeFunnelSchema>;

export type Period = "last_7d" | "last_30d" | "last_90d";
export type Bucket = "day" | "week";

const BASE = "/api/admin/unit-economics";

function periodQS(period: Period): string {
  return `?period=${period}`;
}

export function useEconomicsSummary(period: Period, refetchInterval: number | false) {
  return useQuery({
    queryKey: ["admin-economics", "summary", period],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/summary${periodQS(period)}`,
        schema: SummarySchema,
        signal,
      }),
    refetchInterval,
  });
}

export function useEconomicsTimeseries(
  period: Period,
  bucket: Bucket,
  refetchInterval: number | false,
) {
  return useQuery({
    queryKey: ["admin-economics", "timeseries", period, bucket],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/timeseries${periodQS(period)}&bucket=${bucket}`,
        schema: TimeseriesSchema,
        signal,
      }),
    refetchInterval,
  });
}

export function useEconomicsBySku(period: Period, refetchInterval: number | false) {
  return useQuery({
    queryKey: ["admin-economics", "by_sku", period],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/by_sku${periodQS(period)}`,
        schema: SKUListSchema,
        signal,
      }),
    refetchInterval,
  });
}

export function useEconomicsByReadingType(period: Period, refetchInterval: number | false) {
  return useQuery({
    queryKey: ["admin-economics", "by_reading_type", period],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/by_reading_type${periodQS(period)}`,
        schema: ReadingTypeCostListSchema,
        signal,
      }),
    refetchInterval,
  });
}

export function useEconomicsCohorts(weeks: number, refetchInterval: number | false) {
  return useQuery({
    queryKey: ["admin-economics", "cohorts", weeks],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/cohorts?weeks=${weeks}`,
        schema: CohortsSchema,
        signal,
      }),
    refetchInterval,
  });
}

export function useEconomicsCompositeFunnel(
  period: Period,
  refetchInterval: number | false,
) {
  return useQuery({
    queryKey: ["admin-economics", "composite_funnel", period],
    queryFn: ({ signal }) =>
      request({
        path: `${BASE}/composite_funnel${periodQS(period)}`,
        schema: CompositeFunnelSchema,
        signal,
      }),
    refetchInterval,
  });
}

export const ReconvertResultSchema = z.object({
  converted: z.number().int().nonnegative(),
  skipped_by_currency: z.record(z.number().int().nonnegative()),
});

export function useReconvertAppStore() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request({
        path: `${BASE}/appstore/reconvert`,
        method: "POST",
        schema: ReconvertResultSchema,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-economics"] }),
  });
}
